// src/scripts/composables/useHeaderBackgroundSync.ts

/**
 * Observes the main header (#masthead) for class changes and syncs the
 * 'mainBg' Alpine store value to change the main content background accordingly.
 * This is used to make the main background blue when the header becomes blue
 * (e.g., on scroll for specific header types).
 */
export function useHeaderBackgroundSync(): void {
  const header = document.querySelector<HTMLElement>('#masthead')

  if (!header) {
    console.warn(
      '[useHeaderBackgroundSync] Header element #masthead not found. Background sync disabled.',
    )
    return
  }

  const updateMainBackground = () => {
    // Ensure Alpine and its store are available.
    // This function is intended to be called after Alpine is initialized.
    if (!globalThis.Alpine || !globalThis.Alpine.store) {
      console.warn(
        '[useHeaderBackgroundSync] Alpine.store not available. Cannot update main background.',
      )
      return
    }

    // The class '!bg-bleu-500' is a literal class added to the header
    // by Alpine when isScrolled is true for certain header types.
    if (header.classList.contains('!bg-bleu-500')) {
      globalThis.Alpine.store('mainBg', 'bg-bleu-500')
    } else {
      // Reset to default, which is 'bg-white' as initialized in header.php
      globalThis.Alpine.store('mainBg', 'bg-white')
    }
  }

  // Create a MutationObserver to watch for class changes on the header.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.attributeName === 'class') {
        updateMainBackground()
      }
    }
  })

  // Start observing.
  observer.observe(header, {
    attributes: true,
    attributeFilter: ['class'],
  })

  // Perform an initial sync in case the state is already set on page load
  // and Alpine store is ready.
  updateMainBackground()
}
